import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";

const plans = [
  {
    id: "starter", 
    name: "Starter",
    price: "999",
    desc: "Perfect for small shops with a handful of deliveries a day.",
    drivers: "Up to 3 drivers",
    highlight: false,
    features: [
      "3 driver accounts",
      "Unlimited orders",
      "Real-time GPS tracking",
      "Customer tracking links",
      "Instant status updates",
      "7-day free trial",
    ],
  },
  {
    id: "growth", 
    name: "Growth",
    price: "2,499",
    desc: "For growing businesses that need more drivers and priority support.",
    drivers: "Up to 10 drivers",
    highlight: true,
    badge: "Most Popular",
    features: [
      "10 driver accounts",
      "Unlimited orders",
      "Real-time GPS tracking",
      "Customer tracking links",
      "Pusher live updates",
      "SMS notifications",
      "Priority support",
      "7-day free trial",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "5,999",
    desc: "Unlimited scale for large couriers, supermarket chains, and distributors.",
    drivers: "Unlimited drivers",
    highlight: false,
    features: [
      "Unlimited driver accounts",
      "Unlimited orders",
      "Real-time GPS tracking",
      "Customer tracking links",
      "Pusher live updates",
      "SMS notifications",
      "M-Pesa auto-billing",
      "Dedicated support",
      "7-day free trial",
    ],
  },
];

const faqs = [
  {
    q: "What happens after my 7-day trial?",
    a: "You'll be prompted to choose a plan and pay via M-Pesa. Your data is preserved — nothing gets deleted.",
  },
  {
    q: "Can I change my plan later?",
    a: "Yes, you can upgrade or downgrade at any time. Changes take effect on your next billing cycle.",
  },
  {
    q: "What vehicle types are supported?",
    a: "Motorcycle, bicycle, van, truck, bus, and lorry. If you need another type, reach out.",
  },
  {
    q: "Is the driver app free?",
    a: "Yes — the Driver mobile app is free for your drivers to download and use. It's included in all plans.",
  },
];

export default function PricingPage() {
  return (
    <PublicLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── HERO ── */}
      <section className="bg-slate-900 pt-36 pb-20 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.6) 0%, transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-5">
          <p className="text-orange-400 font-semibold text-sm uppercase tracking-widest mb-4">Pricing</p>
          <h1
            className="text-5xl md:text-6xl font-extrabold text-white mb-5 leading-tight"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Simple, flat-rate pricing
          </h1>
          <p className="text-slate-400 text-lg">
            No per-delivery fees. No surprises. Pay monthly via M-Pesa.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium px-5 py-2 rounded-full">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            All plans include a 7-day free trial — no credit card required
          </div>
        </div>
      </section>

      {/* ── PLANS ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 border transition-all duration-300 ${
                  plan.highlight
                    ? "bg-slate-900 border-orange-500 shadow-xl shadow-orange-500/10 scale-105"
                    : "bg-white border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-1"
                }`}
              >
                {plan.badge && (
                  <div className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                    ⭐ {plan.badge}
                  </div>
                )}

                <p
                  className={`text-lg font-bold mb-1 ${plan.highlight ? "text-white" : "text-slate-900"}`}
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {plan.name}
                </p>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className={`text-sm font-medium ${plan.highlight ? "text-slate-400" : "text-slate-500"}`}>KES</span>
                  <span
                    className={`text-5xl font-extrabold ${plan.highlight ? "text-white" : "text-slate-900"}`}
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.highlight ? "text-slate-400" : "text-slate-500"}`}>/mo</span>
                </div>

                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 ${
                  plan.highlight ? "bg-orange-500/20 text-orange-300" : "bg-slate-100 text-slate-600"
                }`}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  {plan.drivers}
                </div>

                <p className={`text-sm leading-relaxed mb-6 ${plan.highlight ? "text-slate-400" : "text-slate-500"}`}>
                  {plan.desc}
                </p>

                <Link
                  to={`/register?plan=${plan.id}`} 
                  className={`block w-full text-center font-semibold py-3 rounded-xl transition-all duration-200 hover:-translate-y-0.5 mb-7 ${
                    plan.highlight
                      ? "bg-orange-500 hover:bg-orange-400 text-white shadow-lg shadow-orange-500/30"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  Start Free Trial
                </Link>

                <ul className="space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <svg
                        className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-orange-400" : "text-green-500"}`}
                        fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <span className={`text-sm ${plan.highlight ? "text-slate-300" : "text-slate-600"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              💚 All payments processed securely via{" "}
              <span className="text-green-600 font-semibold">M-Pesa</span>.
              No bank card needed.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: "'Sora', sans-serif" }}>
            Common questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border-b border-slate-100 pb-6">
                <p className="font-semibold text-slate-900 mb-2">{faq.q}</p>
                <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Start your free trial today
          </h2>
          <p className="text-white/80 mb-8">7 days, full access, no card required.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-7 py-3.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            Create Free Account
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}